import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Heading } from '@components/shared';
import { activeLinkStyle, containerStyles, linksContainerStyles, linkStyles, titleStyles } from './styles';

interface Link {
  /** Label of the link */
  label: string;
  /** Href of the link (should be a valid relative link) */
  href?: string;
}

export interface ArticleJumpLinksProps {
  /** Links to jump to */
  links: Link[];
  /** Class name for the component */
  className?: string;
  /** Style for the component */
  style?: React.CSSProperties;
  /** Click handler that is fired on link click */
  onClick?: (link: Link) => void;
  /** "On this page", only visible in mobile/tablet */
  title?: string;
}

const DEFAULT_TITLE = 'On this page';

const generateLinkFromLabel = (label: string) => {
  return `#${label.toLowerCase().replace(/ /g, '-')}`;
};

export const ArticleJumpLinks = ({
  links,
  className,
  style,
  onClick,
  title = DEFAULT_TITLE,
}: ArticleJumpLinksProps) => {
  const [activeLink, setActiveLink] = useState<Link | null>(null);

  const handleScroll = useCallback(() => {
    activeLink && setActiveLink(null);
  }, [activeLink]);

  const handleClick = useCallback(
    (link: Link) => {
      setActiveLink(link);
      onClick?.(link);
    },
    [onClick, setActiveLink],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const linksWithHref = useMemo(() => {
    return links.map((link) => {
      const linkHref = link.href ? link.href : generateLinkFromLabel(link.label);
      const isActive = activeLink?.label === link.label;
      const onLinkClick = () => handleClick?.(link);

      return (
        <li key={linkHref} css={[linkStyles, isActive && activeLinkStyle]}>
          <a href={linkHref} onClick={onLinkClick}>
            {link.label}
          </a>
        </li>
      );
    });
  }, [links, activeLink, handleClick]);

  return (
    <div css={containerStyles}>
      <div css={titleStyles}>
        <Heading level={6}>{title}</Heading>
      </div>
      <ul css={linksContainerStyles} className={className} style={style}>
        {linksWithHref}
      </ul>
    </div>
  );
};
